<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import { image } from '@/utils/assets'

export interface PackagingProduct {
    id: string
    name: string
    image?: string
    delivered: number
    received: number
    shipmentDelivered?: number
    shipmentReceived?: number
    hasIssues?: boolean
}

interface Props {
    products: PackagingProduct[]
    variant?: 'transaction' | 'summary'
    emptyMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
    variant: 'transaction',
    emptyMessage: '—',
})

const { t } = useI18n()

function getTruncatedProductName(productName: string | null | undefined): string {
    const name = productName || '—'
    return name.length > 11 ? name.slice(0, 11) + '…' : name
}
</script>

<template>
    <div
        class="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 bg-white dark:bg-dark-400 w-full"
    >
        <template v-if="products.length > 0">
            <table class="w-full table-fixed border-separate [border-spacing:0]">
                <colgroup>
                    <col class="w-[8.25rem]" />
                    <col class="w-[4.25rem]" />
                    <col class="w-[4.25rem]" />
                </colgroup>

                <thead
                    class="bg-green-50 dark:bg-green-900/20 text-xs text-gray-700 dark:text-gray-300"
                >
                    <tr class="align-middle">
                        <th class="px-3 py-2 font-semibold text-left text-black dark:text-white">
                            <div class="flex items-center gap-2 min-w-0">
                                <span
                                    class="truncate font-semibold capitalize"
                                    v-text="t('name')"
                                />
                            </div>
                        </th>
                        <th
                            class="px-2 py-2 font-semibold text-center border-l border-gray-200 dark:border-gray-600 text-black dark:text-white"
                            v-text="t('delivered')"
                        />
                        <th
                            class="px-2 py-2 font-semibold text-center border-l border-gray-200 dark:border-gray-600 text-black dark:text-white"
                            v-text="t('received')"
                        />
                    </tr>
                </thead>

                <tbody class="text-sm divide-y divide-gray-200 dark:divide-gray-600">
                    <tr
                        v-for="(product, productIndex) in products"
                        :key="`${product.id}-${productIndex}`"
                        class="odd:bg-white odd:dark:bg-dark-400 even:bg-gray-50 even:dark:bg-dark-500 align-middle"
                    >
                        <!-- NAME -->
                        <td class="px-3 py-2">
                            <div class="flex items-center gap-2 min-w-0">
                                <div
                                    class="h-7 w-7 shrink-0 rounded bg-gray-200 overflow-hidden flex items-center justify-center"
                                >
                                    <img
                                        v-if="product.image"
                                        :src="image(product.image, 'thumbnail')"
                                        class="h-full w-full object-contain"
                                    />
                                    <mdi:package
                                        v-else
                                        class="w-4 h-4 text-gray-500 dark:text-gray-400"
                                    />
                                </div>

                                <span
                                    v-tooltip="product.name"
                                    class="truncate text-black dark:text-white"
                                    v-text="
                                        props.variant === 'transaction'
                                            ? getTruncatedProductName(product.name)
                                            : product.name
                                    "
                                />

                                <mdi:alert
                                    v-if="product.hasIssues"
                                    v-tooltip="t('productIssueDetected')"
                                    class="w-4 h-4 text-yellow-500 shrink-0"
                                />
                            </div>
                        </td>

                        <!-- DELIVERED (compact cell) -->
                        <td
                            class="px-2 py-2 text-center border-l border-gray-200 dark:border-gray-600 whitespace-nowrap"
                        >
                            <span
                                class="font-semibold text-black dark:text-white"
                                v-text="product.delivered"
                            />
                            <span
                                v-if="product.shipmentDelivered !== undefined"
                                class="text-gray-400 dark:text-white"
                            >
                                ({{ product.shipmentDelivered }})
                            </span>
                        </td>

                        <!-- RECEIVED (compact cell) -->
                        <td
                            class="px-2 py-2 text-center border-l border-gray-200 dark:border-gray-600 whitespace-nowrap"
                        >
                            <span
                                class="font-semibold text-black dark:text-white"
                                v-text="product.received"
                            />
                            <span
                                v-if="product.shipmentReceived !== undefined"
                                class="text-gray-400 dark:text-white"
                            >
                                ({{ product.shipmentReceived }})
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>

        <!-- Empty state -->
        <div
            v-else
            class="py-6 text-center text-gray-400 dark:text-gray-500 text-xs"
            v-text="props.emptyMessage"
        />
    </div>
</template>
